import useCompanyTimeSeries from '@/pages/CompanyReport/components/Past/hooks/useCompanyTimeSeries';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import ReportSubSection from '@/components/ReportSubSection/ReportSubSection';
import { FreeCashFlowChart } from './components/FreeCashFlowChart/FreeCashFlowChart';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { useMount } from '@simplywallst/ui-core';
import { usePastTranslation } from '../../hooks/usePastTranslation';
import {
  getLatestReportedDate,
  getValidTimeSeries,
} from './components/FreeCashFlowChart/utils';
import { format } from 'date-fns';

const FreeCashFlowTable = loadable(
  () => import('./components/FreeCashFlowTable'),
  {
    resolveComponent: (c) => c.FreeCashFlowTable,
  }
);

export const FreeCashFlow = () => {
  const canonicalUrl = useCompanyCanonicalUrl();
  const { data, status } = useCompanyTimeSeries({
    canonicalUrl,
    period: 'MAX',
  });
  const { trackEvent } = useTracking({
    type: 'track',
  });

  useMount(() => {
    trackEvent({
      modifier: 'Free Cash Flow vs Earnings Analysis',
      action: 'viewed',
    });
  });

  const t = usePastTranslation();

  const validTimeSeries = getValidTimeSeries(data?.timeSeries);
  const reportingDate = getLatestReportedDate(validTimeSeries);
  const latestReportedDate = getLatestReportedDate(data?.timeSeries);
  const shouldDisplayDisclaimer =
    reportingDate !== latestReportedDate && reportingDate !== '';

  return (
    <ReportSubSection
      title={t('freeCashFlow.heading')}
      trackingProps={{ subject: 'chart' }}
      bodyRender={() => (
        <>
          {shouldDisplayDisclaimer && (
            <p className="text-pretty text-xs text-softer">
              Note: Latest available free cash flow reconciliation data from{' '}
              {format(reportingDate, 'dd MMM yyyy')}
            </p>
          )}

          <ChartWrapper
            preRenderHeight={350}
            id="free-cash-flow-chart"
            isLoading={status === 'pending'}
            chart={<FreeCashFlowChart rawData={data} status={status} />}
            hideTableScrollbars
            table={<FreeCashFlowTable rawData={data} />}
          />
        </>
      )}
    />
  );
};
